import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useGalleryStore } from '@/stores/useGalleryStore.js';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

export default function useGallery() {
  const controller = ref(new AbortController());

  const galleryStore = useGalleryStore();

  const {
    datasetFilters,
    annotationDisplaySettings,
    imageDisplaySettings,
    reverse,
    currentDatasetID,
    internalCurrentDataset,
    sortBy,
    currentPage,
    imagesPerPage,
    imageList,
    totalImageCount,
    executing,
    combineSequenceFrames,
    isFetchingImages,
  } = storeToRefs(galleryStore);

  const {
    setImageParams,
    setDatasetFilters,
    $reset,
  } = galleryStore;

  // Compute totalPages locally from store refs
  const totalPages = computed(() => Math.ceil(totalImageCount.value / imagesPerPage.value));

  function getImagesParams() {
    const params = {
      get_annotations: true,
      get_sensors: true,
      get_tags: true,
      combine_sequence_frames: combineSequenceFrames.value,
      sort_by: sortBy.value,
      reverse: reverse.value,
      offset: (currentPage.value - 1) * imagesPerPage.value,
      limit: imagesPerPage.value,
    };

    return params;
  }

  function getImagesFilterParams() {
    const params = {
      images_filter: { dataset_id: currentDatasetID.value },
      image_files_filter: {},
      annotations_filter: null,
      image_review_status_filter: null,
      image_tags_filter: null,
      sensors_filter: null,
    };

    // Images filter
    const imagesFilter = params.images_filter;
    if (datasetFilters.value.groupsFilter && datasetFilters.value.groupsFilter.length > 0) {
      imagesFilter.group_ids = datasetFilters.value.groupsFilter;
    }

    // Image files filter
    const imageFilesFilter = params.image_files_filter;
    if (datasetFilters.value.imageNameFilter?.length > 0) {
      imageFilesFilter.image_name = datasetFilters.value.imageNameFilter;
    }
    if (datasetFilters.value.timeRangeFilter) {
      imageFilesFilter.lower_time = datasetFilters.value.timeRangeFilter[0];
      imageFilesFilter.upper_time = datasetFilters.value.timeRangeFilter[1];
    }
    if (datasetFilters.value.dateRangeFilter) {
      if (datasetFilters.value.dateRangeFilter[0]) {
        imageFilesFilter.lower_date = new Date(datasetFilters.value.dateRangeFilter[0]).toISOString();
      }
      if (datasetFilters.value.dateRangeFilter[1]) {
        imageFilesFilter.upper_date = new Date(datasetFilters.value.dateRangeFilter[1]).toISOString();
      }
    }
    if (datasetFilters.value.cameraSourceFilter && datasetFilters.value.cameraSourceFilter.length > 0) {
      imageFilesFilter.image_sources = datasetFilters.value.cameraSourceFilter;
    }

    // Annotations filter
    const annotationsFilter = {};
    if (datasetFilters.value.confidenceRangeFilter) {
      annotationsFilter.lower_score = datasetFilters.value.confidenceRangeFilter[0];
      annotationsFilter.upper_score = datasetFilters.value.confidenceRangeFilter[1];
    }
    if (datasetFilters.value.annotationTypeFilter?.length > 0) {
      annotationsFilter.types = datasetFilters.value.annotationTypeFilter;

      if (datasetFilters.value.annotationTypeFilterInvert) {
        annotationsFilter.types_inverse = true;
      }
    }
    if (datasetFilters.value.annotationSetFilter?.length > 0) {
      annotationsFilter.annotation_set_ids = datasetFilters.value.annotationSetFilter.map((set) => set.id);
    }
    if (datasetFilters.value.imagesWithAnnotationSetFilter?.length > 0) {
      annotationsFilter.images_with_annotation_set_ids = datasetFilters.value.imagesWithAnnotationSetFilter.map((set) => set.id);
    }
    if (datasetFilters.value.annotationLabelFilter?.length > 0) {
      annotationsFilter.images_with_annotation_label_indexes = datasetFilters.value.annotationLabelFilter;
    }
    if (datasetFilters.value.boundingBoxSizeFilter) {
      Object.entries(datasetFilters.value.boundingBoxSizeFilter).forEach(([key, value]) => {
        if (key !== 'type' && value) {
          annotationsFilter[`${key}_${datasetFilters.value.boundingBoxSizeFilter.type}`] = value;
        }
      });
    }
    params.annotations_filter = Object.keys(annotationsFilter).length ? annotationsFilter : null;

    // Tags
    const imageTagFilter = {};
    if (datasetFilters.value.imageTagFilter && datasetFilters.value.imageTagFilter.length > 0) {
      imageTagFilter.tag_names = datasetFilters.value.imageTagFilter.map((tag) => tag.name);
    }
    params.image_tags_filter = Object.keys(imageTagFilter).length ? imageTagFilter : null;

    return params;
  }

  async function getSelectionImageCount(filterParams) {
    const dataConnect = new DatastoreConnect();
    const resp = await dataConnect.getFilteredImages({
      only_count: true,
      combine_sequence_frames: getImagesParams().combine_sequence_frames,
      ...filterParams,
    })
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch((error) => {
        console.error(error);
        return 0;
      });

    return resp;
  }

  async function getImages() {
    if (currentDatasetID.value === 0) {
      totalImageCount.value = 0;
      return [];
    }

    if (internalCurrentDataset.value && internalCurrentDataset.value.parked_at !== "0001-01-01T00:00:00Z") {
      return [];
    }

    isFetchingImages.value = true;
    const dataConnect = new DatastoreConnect();
    controller.value.abort();
    controller.value = new AbortController();

    totalImageCount.value = await getSelectionImageCount(getImagesFilterParams(), controller.value.signal);
    if (totalImageCount.value === 0) {
      isFetchingImages.value = false;
      return [];
    }

    // Handle case when currentPage is greater than the new total number of pages
    if (Math.ceil(totalImageCount.value / imagesPerPage.value) < currentPage.value) {
      let newPage = Math.ceil(totalImageCount.value / imagesPerPage.value);
      if (newPage < 1) {
        newPage = 1;
      }
      currentPage.value = newPage;
      return imageList.value;
    }

    const imageListParams = {
      ...getImagesParams(),
      ...getImagesFilterParams(),
    };
    const resp = await dataConnect.getFilteredImages(imageListParams, controller.value.signal)
      .then((resp) => {
        if (resp.error !== undefined) {
          throw Error(resp.error.message);
        }
        return resp.result;
      })
      .catch((error) => {
        console.error(error);
        return [];
      });

    isFetchingImages.value = false;

    return resp;
  }

  async function deleteFilteredImages(image_ids, sequence_ids) {
    if (currentDatasetID.value === 0) {
      totalImageCount.value = 0;
      return [];
    }

    if (internalCurrentDataset.value && internalCurrentDataset.value.parked_at !== "0001-01-01T00:00:00Z") {
      return [];
    }

    const dataConnect = new DatastoreConnect();
    controller.value.abort();
    controller.value = new AbortController();

    const resp = await dataConnect.deleteFilteredImages({
      ...getImagesParams(),
      ...getImagesFilterParams(),
    });
    if (!resp || resp.error || !resp.result) {
      // TODO: handle error
      return 0;
    }

    return resp.result;
  }

  async function handleGetPreviousImage(currentImageObj) {
    executing.value = true;
    let resultImageObj = currentImageObj;
    const index = imageList.value.findIndex((img) => img.id === currentImageObj.id);
    if ((index - 1) >= 0) {
      const previousImageObj = imageList.value[(index - 1)];
      resultImageObj = previousImageObj;
    } else if (currentPage.value > 1) {
      currentPage.value -= 1;
      // Await new images before setting previous image
      const promise = new Promise((resolve, reject) => {
        getImages().then((list) => {
          imageList.value = list;
          resolve(list);
        });
      });
      await promise.then((list) => {
        const nextImageObj = list[imagesPerPage.value - 1];
        resultImageObj = nextImageObj;
      });
    }
    executing.value = false;
    return resultImageObj;
  }

  async function handleGetNextImage(currentImageObj) {
    executing.value = true;
    let resultImageObj = currentImageObj;
    const index = imageList.value.findIndex((img) => img.id === currentImageObj.id);
    if ((index + 1) < imagesPerPage.value) {
      const nextImageObj = imageList.value[(index + 1)];
      resultImageObj = nextImageObj;
    } else {
      currentPage.value += 1;
      // Await new images before setting next image
      const promise = new Promise((resolve, reject) => {
        getImages().then((list) => {
          imageList.value = list;
          resolve(list);
        });
      });
      await promise.then((list) => {
        const nextImageObj = list[0];
        resultImageObj = nextImageObj;
      });
    }
    executing.value = false;
    return resultImageObj;
  }

  function getHasPreviousImage(currentImageObj) {
    if (!imageList.value) {
      return false;
    }
    if (currentPage.value === 1) {
      const index = imageList.value.findIndex((img) => img.id === currentImageObj.id);
      if (index === 0) {
        return false;
      }
    }
    return true;
  }

  function getHasNextImage(currentImageObj) {
    if (!imageList.value) {
      return false;
    }
    if (currentPage.value === totalPages.value) {
      const index = imageList.value.findIndex((img) => img.id === currentImageObj.id);
      if ((index + 1) === imageList.value.length) {
        return false;
      }
    }
    return true;
  }

  return {
    datasetFilters,
    annotationDisplaySettings,
    imageDisplaySettings,
    reverse,
    combineSequenceFrames,
    sortBy,
    currentDatasetID,
    internalCurrentDataset,
    currentPage,
    totalPages,
    imagesPerPage,
    imageList,
    totalImageCount,
    getImages,
    deleteFilteredImages,
    getImagesParams,
    getImagesFilterParams,
    handleGetPreviousImage,
    handleGetNextImage,
    getHasPreviousImage,
    getHasNextImage,
    setImageParams,
    setDatasetFilters,
    executing,
    isFetchingImages,
    $reset,
  };
}
